import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { ChangableComponent } from '@app/models/changable.component';
import { loadComment, removeComment } from '@app/store/actions/comment.actions';
import { Store } from '@ngrx/store';
import { WebEvent } from 'lingo2-api-models';
import { Comment, Content, IThreadUpdate } from 'lingo2-models';
import { filter, first, takeUntil } from 'rxjs/operators';
import { CommentService } from '../../services/';
import { ContentBlock } from '../../services/interfaces';
import { WebsocketService } from '../../websocket';

interface ContentCommentsChangesType {
  is_commented?: boolean;
  comments_count?: number;
}

type CommentsBlockType = ContentBlock<Comment, object>;

@Component({
  selector: 'app-content-comments',
  templateUrl: './content-comments.component.html',
  styleUrls: ['./content-comments.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContentCommentsComponent extends ChangableComponent implements OnInit, OnDestroy {
  private commentService = inject(CommentService);
  private websocketService = inject(WebsocketService);
  private readonly store = inject(Store);

  @Input() public set content(content: Content) {
    this._content = content;
    this.loadComments();
  }
  public get content(): Content {
    return this._content;
  }
  @Output() public commented = new EventEmitter<boolean>();
  @Output() public changed = new EventEmitter<ContentCommentsChangesType>();
  public comments: CommentsBlockType = {
    items: [],
    loading: false,
    loaded: false,
    pagination: {
      page: 1,
      total: 0,
      pageSize: 5,
      totalPages: 0,
    },
    filter: Object,
  };

  private _content: Content;

  public ngOnInit() {
    this.startWatching();
  }

  public ngOnDestroy() {
    super.ngOnDestroy();
    this.stopWatching();
  }

  private startWatching() {
    this.websocketService.startWatching('thread', this._content.id);

    // уведомление об добавлении/изменении комментариев в нити
    this.websocketService
      .on<IThreadUpdate>(WebEvent.Update)
      .pipe(
        filter((update) => update.thread_id === this._content.id),
        takeUntil(this.destroyed$),
      )
      .subscribe({
        next: (update) => {
          if (update.action === 'delete') {
            this.removeComment(update.comment_id);
          } else {
            this.updateComment(update.comment_id);
          }
          this.changed.emit({
            comments_count: update.comments_count,
          });
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'SOCKET-ERROR');
        },
      });
  }

  private stopWatching() {
    this.websocketService.stopWatching('thread', this._content.id);
  }

  public get isCommentsOpen(): boolean {
    return this._content?.visit_info?.is_access_granted;
  }

  public loadComments(nextPage?: boolean) {
    if (!this._content.id || this.comments.loading) {
      return;
    }

    if (nextPage) {
      this.comments.pagination.page += 1;
    }
    this.comments.loading = true;
    this.commentService
      .getThreadComments(this._content.id, this.comments.filter, this.comments.pagination)
      .pipe(first(), takeUntil(this.destroyed$))
      .subscribe({
        next: (response) => {
          if (nextPage) {
            this.comments.items = [...this.comments.items, ...response.results];
          } else {
            this.comments.items = response.results;
          }

          this.comments.pagination.total = response.total;
          this.comments.pagination.totalPages = response.totalPages;
          this.comments.loading = false;
          this.comments.loaded = true;
          this.changed.emit({
            comments_count: response.total,
          });
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
          this.comments.loading = false;
          this.detectChanges();
        },
      });
  }

  private removeComment(comment_id: string) {
    this.comments.items = this.comments.items.filter((comment) => comment.id !== comment_id);
    this.store.dispatch(removeComment({ comment_id }));
  }

  private updateComment(comment_id: string) {
    this.store.dispatch(loadComment({ thread_id: this._content.id, comment_id }));
  }

  public onCommented() {
    this.comments.pagination.page = 1;
    this.loadComments();
    this.commented.emit(true);
  }

  public onRemoved() {
    this.comments.pagination.page = 1;
    this.loadComments();
  }

  // функция для трекинга списка, чтобы он не 'моргал'
  public trackCommentByFn(index: number, item: Comment): string {
    return item.id;
  }
}
